import { PropsWithChildren } from "react";
import styled from "styled-components";
import tw from "twin.macro";

import heroWaveSrc from "../../assets/images/hero-wave.svg";
import Container from "../common/Container";

const HeroWrapper = styled.div`
  ${tw`relative pt-[150px] pb-[100px]`}

  background-image: url(${heroWaveSrc});
  background-repeat: no-repeat;
  background-size: 100% 57px;
  background-position: bottom left;

  @media (max-width: 1238px) {
    background-size: 1238px 57px;
  }

  &::before {
    ${tw`absolute top-0 -z-20 block w-full h-full`}
    background: linear-gradient(0deg, #eef5ff 0%, #d9e8ff 100%);
    content: "";
  }
`;

const HeroSplitContainer = styled(Container)`
  ${tw`flex flex-col lg:flex-row items-center lg:justify-between`}
  gap: 50px;
`;

const HeroFullContainer = styled(Container)`
  ${tw`flex flex-col items-center py-[30px] text-center`}
`;

const heroWidthFull = 1200;

export const HeroSplitLeft = styled.div<{ fullWidth: number }>`
  ${tw`place-content-center text-left z-10 flex-shrink-0`}
  width: ${(props) => (props.fullWidth / heroWidthFull) * 100}%;
  max-width: ${(props) => props.fullWidth}px;

  @media (max-width: 1023px) {
    ${tw`text-center`}
    width: ${(props) => props.fullWidth}px;
    width: auto;
  }
`;

export const HeroSplitRight = styled.div`
  ${tw`hidden flex-grow lg:flex flex-col self-stretch items-center justify-center`}
`;

export const HeroTag = styled.div`
  ${tw`uppercase text-blue-500 text-[16px] leading-[30px] font-black mb-[12px]`}
  letter-spacing: 2px;
`;

export const HeroHeadline = styled.h2`
  ${tw`text-theme-gray-1 font-title mt-0`}
  font-size: 44px;
  line-height: 57px;
  letter-spacing: -1px;

  @media (max-width: 950px) {
    font-size: 42px;
    line-height: 48px;
  }
`;

export const BiggerHeroHeadline = styled.h2`
  ${tw`text-theme-gray-1 font-title mt-0`}
  font-size: 62px;
  line-height: 72px;
  font-weight: 400;
  letter-spacing: -1px;

  @media (max-width: 950px) {
    font-size: 44px;
    line-height: 52px;
  }
`;

export const HeroHeadlineHighlight = styled.span`
  ${tw`relative text-blue-500 font-extrabold`}
  letter-spacing: -0.25px;
`;

export const HeroSubheadline = styled.p`
  ${tw`font-normal font-subtitle text-gray-800`}
  font-size: 21px;
  line-height: 36px;

  @media (max-width: 950px) {
    font-size: 21px;
  }
`;

const Hero: React.FunctionComponent<PropsWithChildren<{ split?: boolean }>> = ({
  children,
  split = true,
  ...props
}) => (
  <HeroWrapper>
    {split ? (
      <HeroSplitContainer {...props}>{children}</HeroSplitContainer>
    ) : (
      <HeroFullContainer {...props}>{children}</HeroFullContainer>
    )}
  </HeroWrapper>
);

export default Hero;
